/* Zeus variant cards.
 *
 * Puts each option's own price on its tile, so the choice is legible without
 * selecting it first - the behaviour the Custom Cerakote Barbell page has.
 * That page does this inside zeus-colour-picker.js because it also owns the
 * colour grid; this is the standalone version for ordinary variant products.
 *
 * Reads the variant table emitted by zeus-variant-cards.liquid. Works for
 * multi-option products too: the price shown for a value is the variant you
 * would land on if you picked it while leaving the other options as they are.
 */
(() => {
  const root = document.querySelector('[data-zeus-vcards]');
  const dataEl = document.getElementById('zeus-vcards-data');
  if (!root || !dataEl) return;

  let data;
  try {
    data = JSON.parse(dataEl.textContent);
  } catch {
    return;
  }
  if (!data?.variants?.length) return;

  // Options listed in the product's custom.card_picker_delta metafield show the
  // price DIFFERENCE instead of the full configured total. On an add-on option
  // like Spotter Arms, the total (CA$1,495) reads as the add-on's price; the
  // difference (+$200) is what the customer actually wants to know.
  //
  // The difference is measured against the CHEAPEST choice in that option, not
  // the current selection - so the numbers stay put as the customer clicks
  // around instead of flipping sign. The cheapest choice reads 'Included'.
  const deltaOpts = new Set(String(data.delta || '').split(',').map((x) => x.trim().toLowerCase()).filter(Boolean));
  const isDelta = (index) => deltaOpts.has(String(data.options?.[index] || '').toLowerCase());
  const deltaLabel = (cents) => {
    if (cents <= 0) return 'Included';
    const whole = cents % 100 === 0;
    return '+$' + (whole ? (cents / 100).toLocaleString('en-CA') : (cents / 100).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  };

  const money = (cents) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: data.currency || 'CAD' }).format(cents / 100);

  const fieldsets = () => [...root.querySelectorAll('.product-form__input--pill')];

  /* The value currently chosen in each option position. */
  const selection = () =>
    fieldsets().map((fs) => fs.querySelector('input[type="radio"]:checked')?.value ?? null);

  /* The variant you would land on by choosing `value` at `index`, holding the
     other options where they are. If that exact combination does not exist
     (ragged option grids), fall back to the cheapest variant carrying this
     value so the tile still says something true. */
  const variantFor = (index, value) => {
    const sel = selection();
    sel[index] = value;
    const exact = data.variants.find((v) => v.o.every((ov, i) => sel[i] == null || ov === sel[i]));
    if (exact) return exact;
    const any = data.variants.filter((v) => v.o[index] === value).sort((a, b) => a.p - b.p);
    return any.length ? any[0] : null;
  };

  const label = () => {
    fieldsets().forEach((fs, index) => {
      // An option whose values run long gets its own single column; the others
      // keep the two-column grid. Decided per option, not per product.
      const longest = Math.max(0, ...[...fs.querySelectorAll('input[type="radio"]')].map((i) => i.value.length));
      fs.classList.toggle('zeus-vcards__wide', longest > 28);

      // Echo the current choice beside the option name, so the whole configuration
      // can be confirmed from the three headings without re-reading the tiles.
      const legend = fs.querySelector('legend, .form__label');
      const chosen = fs.querySelector('input[type="radio"]:checked');
      if (legend && chosen) {
        let cur = legend.querySelector('.zeus-pill__current');
        if (!cur) { cur = document.createElement('span'); cur.className = 'zeus-pill__current'; legend.appendChild(cur); }
        cur.textContent = chosen.value;
      }
      for (const input of fs.querySelectorAll('input[type="radio"]')) {
        const el = document.querySelector('label[for="' + CSS.escape(input.id) + '"]');
        if (!el) continue;
        const match = variantFor(index, input.value);
        if (!match) continue;

        // Dawn puts a visually-hidden "Variant sold out or unavailable" span in
        // the label. Build the name from the input's own value and PRESERVE that
        // span rather than flattening textContent, which would swallow it into
        // the visible tile name.
        const hidden = el.querySelector('.label-unavailable');
        const name = document.createElement('span');
        name.className = 'zeus-pill__name';
        name.textContent = input.value;
        const price = document.createElement('span');
        price.className = 'zeus-pill__price';
        price.textContent = money(match.p);
        el.textContent = '';
        if (isDelta(index)) {
          const inputs = [...fs.querySelectorAll('input[type="radio"]')];
          const prices = inputs.map((i) => variantFor(index, i.value)?.p).filter((x) => x != null);
          const floor = prices.length ? Math.min(...prices) : match.p;
          price.textContent = deltaLabel(match.p - floor);
          price.classList.add('zeus-pill__price--delta');
          el.append(name, price);
          if (hidden) el.appendChild(hidden);
          continue;
        }
        el.append(name, price);
        // The parent variants carry their component's SKU, so the tile can name
        // the actual part being ordered. Absent on products whose parents have
        // no SKU (the Cerakote barbell), where the line is simply omitted.
        if (match.s) {
          const sku = document.createElement('span');
          sku.className = 'zeus-pill__sku';
          sku.textContent = 'SKU: ' + match.s;
          el.append(sku);
        }
        if (hidden) el.appendChild(hidden);
      }
    });
  };

  // Dawn replaces the variant-picker markup on every change, wiping what we
  // injected - so re-label after each one. Capture, because the picker element
  // itself is swapped out.
  const section = root.closest('[data-section-id], section') || document;
  section.addEventListener('change', () => setTimeout(label, 60), true);
  label();
})();
